import { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  cx,
  Field,
  Icon,
  Input,
  NumberInput,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Text,
  Title,
  UploadButton,
  useToast,
} from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import {
  fetchProductMediaRaw,
  getComponents,
  getProductCertifications,
  getProductPartners,
  getSupplyChain,
  listCertifications,
  listFacilities,
  listPartners,
  putComponents,
  putProductCertifications,
  putProductPartners,
  putSupplyChain,
  uploadProductMedia,
} from "../api/dpp.js";
import type {
  Certification,
  ComponentNodeBody,
  ComponentRowDto,
  Facility,
  Partner,
  StageBody,
  StageTier,
  SupplyChainStageKind,
} from "../api/dpp.js";
import type { AccessGrantRole } from "../types.js";
import { AudienceField } from "./AudienceField.js";
import { useT } from "../i18n.js";
import { usePartnerCategoryLabel } from "./partnerCategories.js";
import { useGoogleMaps } from "../maps/loader.js";
import { PinsMap } from "../maps/PinsMap.js";

const STAGE_KINDS: SupplyChainStageKind[] = [
  "design",
  "raw_material",
  "spinning",
  "weaving",
  "dyeing",
  "sampling",
  "trims",
  "manufacturing",
  "delivery",
];

interface StageDraft {
  stage: SupplyChainStageKind;
  facilityId: string;
  transportMethod: string;
  notes: string;
  tier: StageTier;
  audience: AccessGrantRole[];
}

/**
 * Ordered chain builder rendered as a numbered timeline: one bordered card per
 * stage with labelled fields, reorder/delete controls, and a sticky save bar.
 * Save is still a replace-all PUT (stages + product certs + product partners).
 */
export function SupplyChainCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const partnerCategoryLabel = usePartnerCategoryLabel();
  const toast = useToast();
  const errorText = useErrorText();
  const mapsNs = useGoogleMaps();
  const [stages, setStages] = useState<StageDraft[] | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [certs, setCerts] = useState<Certification[]>([]);
  const [productCertIds, setProductCertIds] = useState<string[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [productPartnerIds, setProductPartnerIds] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [chain, facs, orgCerts, prodCerts, orgPartners, prodPartners] = await Promise.all([
          getSupplyChain(orgId, productId),
          listFacilities(orgId),
          listCertifications(orgId),
          getProductCertifications(orgId, productId),
          listPartners(orgId),
          getProductPartners(orgId, productId),
        ]);
        setStages(
          chain.map((s) => ({
            stage: s.stage,
            facilityId: s.facilityId ?? "",
            transportMethod: s.transportMethod ?? "",
            notes: s.notes ?? "",
            tier: s.tier,
            audience: (s.audience ?? []) as AccessGrantRole[],
          })),
        );
        setFacilities(facs);
        setCerts(orgCerts);
        setProductCertIds(prodCerts.map((c) => c.id));
        setPartners(orgPartners);
        setProductPartnerIds(prodPartners.map((p) => p.id));
      } catch (e) {
        setError(e);
      }
    })();
  }, [orgId, productId]);

  const update = (idx: number, patch: Partial<StageDraft>) => {
    setStages((prev) => prev!.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const move = (idx: number, delta: -1 | 1) => {
    setStages((prev) => {
      const next = [...prev!];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return next;
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  };
  const toggleId = (setter: typeof setProductCertIds, id: string, on: boolean) =>
    setter((prev) => (on ? [...prev, id] : prev.filter((x) => x !== id)));

  const save = async () => {
    setSaving(true);
    try {
      const body: StageBody[] = stages!.map((s) => ({
        stage: s.stage,
        tier: s.tier,
        audience: s.tier === "restricted" && s.audience.length > 0 ? s.audience : null,
        facilityId: s.facilityId || null,
        transportMethod: s.transportMethod.trim() || null,
        notes: s.notes.trim() || null,
      }));
      await putSupplyChain(orgId, productId, body);
      await putProductCertifications(orgId, productId, productCertIds);
      await putProductPartners(orgId, productId, productPartnerIds);
      toast.success(t("chain.saved"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  if (error != null) return <ErrorAlert error={error} />;
  if (stages === null) return <Spin />;

  const facilityOptions = [
    { label: t("chain.noFacility"), value: "" },
    ...facilities.map((f) => ({
      label: `${f.name} (${[f.city, f.countryIso].filter(Boolean).join(", ")})`,
      value: f.id,
    })),
  ];
  const tierOptions = [
    { label: t("tier.public"), value: "public" },
    { label: t("tier.restricted"), value: "restricted" },
  ];

  // Ordered, deduplicated route pins from the CURRENT drafts — the map follows
  // edits live, mirroring what the published passport page will show.
  const seenPin = new Set<string>();
  const chainPins = stages.flatMap((s) => {
    const f = facilities.find((x) => x.id === s.facilityId);
    if (!f || f.lat === null || f.lng === null) return [];
    const key = `${f.lat},${f.lng}`;
    if (seenPin.has(key)) return [];
    seenPin.add(key);
    return [{ lat: f.lat, lng: f.lng, label: f.name }];
  });

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Text secondary>{t("chain.hint")}</Text>

      {/* Stages — numbered production timeline */}
      <section className="sc-section">
        <div className="sc-section-head">
          <Title level={5}>{t("chain.stagesTitle")}</Title>
          {stages.length > 0 && <Tag>{t("chain.stageCount").replace("{n}", String(stages.length))}</Tag>}
        </div>

        {stages.length === 0 ? (
          <Text secondary>{t("chain.empty")}</Text>
        ) : (
          <div className="sc-timeline">
            {stages.map((s, idx) => (
              <div key={idx} className="sc-stage">
                <div className="sc-rail">
                  <div className="sc-ord">{idx + 1}</div>
                  <div className="sc-connector" />
                </div>
                <div className="sc-card">
                  <div className="sc-card-head">
                    <div className="sc-stage-select">
                      <Select
                        options={STAGE_KINDS.map((k) => ({ label: t(`stage.${k}`), value: k }))}
                        value={s.stage}
                        disabled={!canWrite}
                        onChange={(v) => update(idx, { stage: v as SupplyChainStageKind })}
                      />
                    </div>
                    <Tag color={s.tier === "restricted" ? "warning" : "success"}>{t(`tier.${s.tier}`)}</Tag>
                    {canWrite && (
                      <div className="sc-card-actions">
                        <Button
                          size="sm"
                          variant="subtle"
                          aria-label={t("common.moveUp")}
                          disabled={idx === 0}
                          onClick={() => move(idx, -1)}
                        >
                          <Icon name="chevron-up" size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="subtle"
                          aria-label={t("common.moveDown")}
                          disabled={idx === stages.length - 1}
                          onClick={() => move(idx, 1)}
                        >
                          <Icon name="chevron-down" size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="subtle"
                          aria-label={t("common.remove")}
                          onClick={() => setStages((prev) => prev!.filter((_, i) => i !== idx))}
                        >
                          <Icon name="trash" size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="sc-grid">
                    <Field label={t("chain.facility")}>
                      <Select
                        options={facilityOptions}
                        value={s.facilityId}
                        showSearch
                        disabled={!canWrite}
                        onChange={(v) => update(idx, { facilityId: v })}
                      />
                    </Field>
                    <Field label={t("tier.label")}>
                      <Select
                        options={tierOptions}
                        value={s.tier}
                        disabled={!canWrite}
                        onChange={(v) => update(idx, { tier: v as StageTier })}
                      />
                    </Field>
                    {s.tier === "restricted" && (
                      <AudienceField value={s.audience} onChange={(a) => update(idx, { audience: a })} />
                    )}
                    {s.stage === "delivery" && (
                      <Field className="sc-span2" label={t("chain.transport")}>
                        <Input
                          value={s.transportMethod}
                          disabled={!canWrite}
                          onChange={(e) => update(idx, { transportMethod: e.target.value })}
                        />
                      </Field>
                    )}
                    <Field className="sc-span2" label={t("chain.notes")}>
                      <Input
                        value={s.notes}
                        disabled={!canWrite}
                        onChange={(e) => update(idx, { notes: e.target.value })}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {canWrite && (
          <div className="sc-add">
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() =>
                setStages((prev) => [
                  ...prev!,
                  {
                    stage: "design",
                    facilityId: "",
                    transportMethod: "",
                    notes: "",
                    tier: "public",
                    audience: [],
                  },
                ])
              }
            >
              {t("chain.addStage")}
            </Button>
          </div>
        )}
      </section>

      {/* Live route preview of the stages above (facilities with coordinates only) */}
      {mapsNs != null && chainPins.length > 0 && (
        <section className="sc-section">
          <div className="sc-section-head">
            <Title level={5}>{t("chain.mapTitle")}</Title>
          </div>
          <PinsMap pins={chainPins} chain height={300} />
        </section>
      )}

      {/* Product certifications */}
      {certs.length > 0 && (
        <section className="sc-section">
          <div className="sc-section-head">
            <Title level={5}>{t("chain.productCerts")}</Title>
            {productCertIds.length > 0 && <Tag color="primary">{productCertIds.length}</Tag>}
          </div>
          <Text small secondary>
            {t("chain.certsHint")}
          </Text>
          <div className="sc-chips" style={{ marginTop: "var(--az-sp-3)" }}>
            {certs.map((c) => {
              const on = productCertIds.includes(c.id);
              return (
                <div key={c.id} className={cx("sc-chip", on && "is-on")}>
                  <Checkbox
                    disabled={!canWrite}
                    checked={on}
                    onChange={(e) => toggleId(setProductCertIds, c.id, e.target.checked)}
                  >
                    {c.scheme}
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Circularity partners */}
      {partners.length > 0 && (
        <section className="sc-section">
          <div className="sc-section-head">
            <Title level={5}>{t("chain.productPartners")}</Title>
            {productPartnerIds.length > 0 && <Tag color="primary">{productPartnerIds.length}</Tag>}
          </div>
          <Text small secondary>
            {t("chain.partnersHint")}
          </Text>
          <div className="sc-chips" style={{ marginTop: "var(--az-sp-3)" }}>
            {partners.map((p) => {
              const on = productPartnerIds.includes(p.id);
              return (
                <div key={p.id} className={cx("sc-chip", on && "is-on")}>
                  <Checkbox
                    disabled={!canWrite}
                    checked={on}
                    onChange={(e) => toggleId(setProductPartnerIds, p.id, e.target.checked)}
                  >
                    {p.name} ({partnerCategoryLabel(p.category)})
                  </Checkbox>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {canWrite && (
        <div className="pd-savebar">
          <Text small secondary>
            {t("chain.unsaved")}
          </Text>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            {t("chain.save")}
          </Button>
        </div>
      )}
    </Space>
  );
}

/* --- Component tree editor ---------------------------------------------------- */

interface ComponentDraft {
  key: string;
  name: string;
  componentType: string;
  weightG: string;
  materialNote: string;
  facilityId: string;
  imageMediaId: string;
  isSeparable: boolean;
  tier: StageTier;
  audience: AccessGrantRole[];
  children: ComponentDraft[];
}

let draftKey = 0;
const newDraft = (): ComponentDraft => ({
  key: `c${draftKey++}`,
  name: "",
  componentType: "",
  weightG: "",
  materialNote: "",
  facilityId: "",
  imageMediaId: "",
  isSeparable: false,
  tier: "public",
  audience: [],
  children: [],
});

function rowsToTree(rows: ComponentRowDto[]): ComponentDraft[] {
  const toDraft = (row: ComponentRowDto): ComponentDraft => ({
    key: row.id,
    name: row.name,
    componentType: row.componentType ?? "",
    weightG: row.weightG === null ? "" : String(row.weightG),
    materialNote: row.materialNote ?? "",
    facilityId: row.facilityId ?? "",
    imageMediaId: row.imageMediaId ?? "",
    isSeparable: row.isSeparable ?? false,
    tier: row.tier,
    audience: (row.audience ?? []) as AccessGrantRole[],
    children: rows.filter((r) => r.parentId === row.id).map(toDraft),
  });
  return rows.filter((r) => r.parentId === null).map(toDraft);
}

function treeToBody(nodes: ComponentDraft[]): ComponentNodeBody[] {
  return nodes
    .filter((n) => n.name.trim() !== "")
    .map((n) => ({
      name: n.name.trim(),
      tier: n.tier,
      ...(n.tier === "restricted" && n.audience.length > 0 ? { audience: n.audience } : { audience: null }),
      componentType: n.componentType.trim() || null,
      weightG: n.weightG.trim() === "" ? null : Number(n.weightG),
      materialNote: n.materialNote.trim() || null,
      facilityId: n.facilityId || null,
      imageMediaId: n.imageMediaId || null,
      isSeparable: n.isSeparable,
      ...(n.children.length > 0 ? { children: treeToBody(n.children) } : {}),
    }));
}

const collectMediaIds = (nodes: ComponentDraft[]): string[] =>
  nodes.flatMap((n) => [...(n.imageMediaId ? [n.imageMediaId] : []), ...collectMediaIds(n.children)]);
const countNodes = (nodes: ComponentDraft[]): number =>
  nodes.reduce((sum, n) => sum + 1 + countNodes(n.children), 0);

/**
 * Bill-of-materials editor rendered as a nested tree of node cards: a thumbnail +
 * live title header, labelled fields, and children nested under a connector rail.
 * Thumbnails are fetched through the authenticated raw route (blob object-URLs),
 * mirroring MediaCard, because the public /media/ URL only resolves after publish.
 */
export function ComponentsCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const [tree, setTree] = useState<ComponentDraft[] | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const urlsRef = useRef<Record<string, string>>({});

  const ensureThumb = async (mediaId: string) => {
    if (urlsRef.current[mediaId]) return;
    try {
      const blob = await fetchProductMediaRaw(orgId, productId, mediaId);
      const url = URL.createObjectURL(blob);
      urlsRef.current[mediaId] = url;
      setThumbs((prev) => ({ ...prev, [mediaId]: url }));
    } catch {
      /* Thumbnails are best-effort; a missing preview must not block editing. */
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        const [rows, facs] = await Promise.all([getComponents(orgId, productId), listFacilities(orgId)]);
        const built = rowsToTree(rows);
        setTree(built);
        setFacilities(facs);
        for (const id of collectMediaIds(built)) void ensureThumb(id);
      } catch (e) {
        setError(e);
      }
    })();
    const urls = urlsRef.current;
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
      urlsRef.current = {};
    };
  }, [orgId, productId]);

  /** Immutable node update by key path. */
  const mapTree = (
    nodes: ComponentDraft[],
    key: string,
    fn: (n: ComponentDraft) => ComponentDraft | null,
  ): ComponentDraft[] =>
    nodes.flatMap((n) => {
      if (n.key === key) {
        const next = fn(n);
        return next === null ? [] : [next];
      }
      return [{ ...n, children: mapTree(n.children, key, fn) }];
    });
  const patch = (key: string, fn: (n: ComponentDraft) => ComponentDraft) =>
    setTree((prev) => mapTree(prev!, key, fn));

  const save = async () => {
    setSaving(true);
    try {
      await putComponents(orgId, productId, treeToBody(tree!));
      toast.success(t("comps.saved"));
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (key: string, file: File) => {
    try {
      const media = await uploadProductMedia(orgId, productId, file, "component");
      const url = URL.createObjectURL(file);
      urlsRef.current[media.id] = url;
      setThumbs((prev) => ({ ...prev, [media.id]: url }));
      patch(key, (x) => ({ ...x, imageMediaId: media.id }));
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (error != null) return <ErrorAlert error={error} />;
  if (tree === null) return <Spin />;

  const facilityOptions = [
    { label: t("chain.noFacility"), value: "" },
    ...facilities.map((f) => ({ label: f.name, value: f.id })),
  ];
  const tierOptions = [
    { label: t("tier.public"), value: "public" },
    { label: t("tier.restricted"), value: "restricted" },
  ];

  const renderNode = (n: ComponentDraft) => {
    const thumb = n.imageMediaId ? thumbs[n.imageMediaId] : undefined;
    return (
      <div key={n.key}>
        <div className="cmp-node">
          <div className="cmp-node-head">
            {thumb ? (
              <img className="cmp-thumb" src={thumb} alt="" />
            ) : (
              <div className="cmp-thumb cmp-thumb-empty">
                <Icon name="upload" size={18} />
              </div>
            )}
            <div className="cmp-node-title">
              <Text strong>{n.name.trim() || t("comps.name")}</Text>
              {n.componentType.trim() && (
                <Text small secondary>
                  {" · "}
                  {n.componentType}
                </Text>
              )}
            </div>
            <Tag color={n.tier === "restricted" ? "warning" : "success"}>{t(`tier.${n.tier}`)}</Tag>
            {canWrite && (
              <div className="cmp-node-actions">
                <Button
                  size="sm"
                  variant="subtle"
                  aria-label={t("common.remove")}
                  onClick={() => setTree((prev) => mapTree(prev!, n.key, () => null))}
                >
                  <Icon name="trash" size={14} />
                </Button>
              </div>
            )}
          </div>

          <div className="cmp-grid">
            <Field label={t("comps.name")}>
              <Input
                value={n.name}
                disabled={!canWrite}
                onChange={(e) => patch(n.key, (x) => ({ ...x, name: e.target.value }))}
              />
            </Field>
            <Field label={t("comps.type")}>
              <Input
                value={n.componentType}
                disabled={!canWrite}
                onChange={(e) => patch(n.key, (x) => ({ ...x, componentType: e.target.value }))}
              />
            </Field>
            <Field label={t("comps.weight")}>
              <NumberInput
                suffix="g"
                value={n.weightG}
                disabled={!canWrite}
                onChange={(v) => patch(n.key, (x) => ({ ...x, weightG: v }))}
              />
            </Field>
            <Field label={t("comps.facility")}>
              <Select
                options={facilityOptions}
                value={n.facilityId}
                showSearch
                disabled={!canWrite}
                onChange={(v) => patch(n.key, (x) => ({ ...x, facilityId: v }))}
              />
            </Field>
            <Field label={t("tier.label")}>
              <Select
                options={tierOptions}
                value={n.tier}
                disabled={!canWrite}
                onChange={(v) => patch(n.key, (x) => ({ ...x, tier: v as StageTier }))}
              />
            </Field>
            {n.tier === "restricted" && (
              <AudienceField
                value={n.audience}
                onChange={(a) => patch(n.key, (x) => ({ ...x, audience: a }))}
              />
            )}
            <Field label={t("comps.separable")}>
              <Switch
                checked={n.isSeparable}
                disabled={!canWrite}
                onChange={(e) => patch(n.key, (x) => ({ ...x, isSeparable: e.target.checked }))}
              >
                {n.isSeparable ? t("common.yes") : t("common.no")}
              </Switch>
            </Field>
            <Field className="cmp-span2" label={t("comps.materialNote")}>
              <Input
                value={n.materialNote}
                disabled={!canWrite}
                onChange={(e) => patch(n.key, (x) => ({ ...x, materialNote: e.target.value }))}
              />
            </Field>
            <Field className="cmp-span2" label={t("comps.image")}>
              <div className="cmp-img-row">
                {n.imageMediaId ? (
                  <>
                    <UploadButton
                      accept="image/jpeg,image/png,image/webp"
                      size="sm"
                      disabled={!canWrite}
                      onSelect={(file) => void uploadImage(n.key, file)}
                    >
                      {t("comps.replaceImage")}
                    </UploadButton>
                    <Button
                      size="sm"
                      variant="subtle"
                      disabled={!canWrite}
                      icon={<Icon name="close" size={12} />}
                      onClick={() => patch(n.key, (x) => ({ ...x, imageMediaId: "" }))}
                    >
                      {t("comps.removeImage")}
                    </Button>
                  </>
                ) : (
                  <UploadButton
                    accept="image/jpeg,image/png,image/webp"
                    size="sm"
                    disabled={!canWrite}
                    onSelect={(file) => void uploadImage(n.key, file)}
                  >
                    {t("comps.image")}
                  </UploadButton>
                )}
              </div>
            </Field>
          </div>

          {canWrite && (
            <div style={{ marginTop: "var(--az-sp-3)" }}>
              <Button
                size="sm"
                variant="subtle"
                icon={<Icon name="plus" size={14} />}
                onClick={() => patch(n.key, (x) => ({ ...x, children: [...x.children, newDraft()] }))}
              >
                {t("comps.addChild")}
              </Button>
            </div>
          )}
        </div>

        {n.children.length > 0 && <div className="cmp-children">{n.children.map(renderNode)}</div>}
      </div>
    );
  };

  const total = countNodes(tree);

  return (
    <Space direction="vertical" size="lg" style={{ width: "100%" }}>
      <Text secondary>{t("comps.hint")}</Text>

      <section className="sc-section">
        <div className="sc-section-head">
          <Title level={5}>{t("comps.rootTitle")}</Title>
          {total > 0 && <Tag>{t("comps.compCount").replace("{n}", String(total))}</Tag>}
        </div>

        {tree.length === 0 ? (
          <Text secondary>{t("comps.empty")}</Text>
        ) : (
          <div className="cmp-tree">{tree.map(renderNode)}</div>
        )}

        {canWrite && (
          <div style={{ marginTop: "var(--az-sp-4)" }}>
            <Button
              size="sm"
              icon={<Icon name="plus" size={14} />}
              onClick={() => setTree((prev) => [...prev!, newDraft()])}
            >
              {t("comps.add")}
            </Button>
          </div>
        )}
      </section>

      {canWrite && (
        <div className="pd-savebar">
          <Text small secondary>
            {total > 0 ? t("comps.compCount").replace("{n}", String(total)) : ""}
          </Text>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            {t("comps.save")}
          </Button>
        </div>
      )}
    </Space>
  );
}
